import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, Observable, startWith, switchMap } from 'rxjs';
import { filterNil } from '@ngneat/elf';
import { CallParticipant, ParticipantEventAction } from '../common/interfaces/rtc-interface';
import {
  defaultInboundThresholds,
  defaultMediaDeviceState,
  defaultOutboundThresholds,
  DevicesState,
  NetworkMetricsThresholds,
  NetworkQualityParams,
  NetworkStatsReportScore,
  TrafficDirection,
} from '../utilities/webrtc-stats-reporter/webrtc-stats-reporter-types';
import { SubjectMap } from '../sessions/common/session-data-structures';
import { ObservableUtils } from '../utilities/ObservableUtils';
import { DeviceType } from '../common/utils/devices-handle-util';
import { ProviderStateService } from './provider-state.service';
import { FLAGS, FlagsService } from './flags.service';
import { SystemStatusAnalyticsService } from './system-status-analytics.service';

export interface ParticipantNetworkState {
  isLocal: boolean;
  isNetworkStable: boolean;
}

export interface ParticipantSystemStatus {
  networkState?: ParticipantNetworkState;
  devicesState?: DevicesState;
  isDeviceStruggling?: boolean;
}

const PARTICIPANTS_SYSTEM_STATUS_BUFFER_DURATION = 500;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ParticipantsSystemStateService {
  private participantSystemStateMap: SubjectMap<ParticipantSystemStatus> = new SubjectMap();

  constructor(
    private providerState: ProviderStateService,
    private observerUtils: ObservableUtils,
    private flagsService: FlagsService,
    private systemStatusAnalyticsService: SystemStatusAnalyticsService,
  ) {
    this.listenToParticipantUpdatedEvents();
  }

  private listenToParticipantUpdatedEvents() {
    this.providerState.participantEvents$
      .pipe(
        untilDestroyed(this),
        filter((participantEvent) => participantEvent?.action === ParticipantEventAction.LEFT),
      )
      .subscribe((participantEvent) => {
        const action = participantEvent?.action;
        const participant = participantEvent?.participant;

        if (!action || !participant) {
          return;
        }

        // Clear participants entries on leave
        if (participant.local) {
          this.resetAllParticipantSystemStates();
        } else {
          this.participantSystemStateMap.get(participant.participantId).next(null);
        }
      });

    // update participants system status including network scores and devices states
    this.flagsService
      .featureFlagChanged(FLAGS.ENABLE_NETWORK_INDICATOR)
      .pipe(
        untilDestroyed(this),
        filter(
          // The system status indicator won't be shown if the flag is disabled
          (isFlagEnabled) => isFlagEnabled,
        ),
        switchMap(() => this.participantsSystemStatus$()),
      )
      .subscribe((participants: any[]) => {
        this.updateParticipantsSystemState(participants);
      });
  }

  // Given participantMetadataChange$ will emit many values every 10 seconds (Network stats fetch), it's better to do the analysis in one time
  private participantsSystemStatus$(): Observable<(CallParticipant | null)[]> {
    return this.observerUtils.bufferedObservableOf(
      this.providerState.participantMetadataChange$,
      PARTICIPANTS_SYSTEM_STATUS_BUFFER_DURATION,
    );
  }

  private updateParticipantsSystemState(participants: any[]) {
    participants.forEach((participant) => {
      const participantNetworkState = this.getParticipantNetworkState(participant);
      const participantDevicesState = this.getParticipantDevicesState(participant);
      const isDeviceStruggling = this.getDevicePerformanceState(participant);

      const systemStatus = {
        networkState: participantNetworkState,
        devicesState: participantDevicesState,
        isDeviceStruggling: isDeviceStruggling,
      };

      this.participantSystemStateMap.get(participant?.participantId).next(systemStatus);

      // Only send analytics for local user
      if (participant?.local) {
        this.addToSystemStatusBuffer(systemStatus);
      }
    });
  }

  private getParticipantNetworkState(participant: any) {
    if (this.shouldReflectNetworkState()) {
      return this.calculateParticipantNetworkState(
        participant?.userSystemStatus?.networkStatsScore,
        participant?.local ?? false,
      );
    }
  }

  private getParticipantDevicesState(participant: any) {
    if (this.shouldReflectDevicesState()) {
      return participant?.userSystemStatus?.devicesState;
    }
  }

  private calculateParticipantNetworkState(
    networkStatsScore: NetworkStatsReportScore | undefined,
    isLocal: boolean,
  ): ParticipantNetworkState | undefined {
    if (!networkStatsScore) {
      return;
    }

    // Depend only on outbound scores if exist
    if (networkStatsScore.outbound) {
      return this.checkOutboundScores(networkStatsScore.outbound, isLocal);
    }
    return this.checkInboundScores(networkStatsScore.inbound, isLocal);
  }

  // Article about MOS usage in WebRTC: https://medium.com/@evgmel/bad-internet-connection-and-how-to-detect-it-in-a-webrtc-call-cfdf655dbd49
  private calculateMOS(
    networkQualityParams: NetworkQualityParams | undefined,
    reducePacketLossEffect: boolean,
  ): number {
    if (!networkQualityParams) {
      return -1;
    }

    // Calculating the effective delay by considering Round Trip Time (RTT), and Avg Jitter (The variation in packet arrival times)
    // There is 10ms is added to account for typical encoding and processing delays at the sender and receiver
    // Avg Jitter is multipled by 2 to consider the jitter buffer delays (Jitter buffer is used to smooth the exprience if there is a variation in packet arrival times)
    const effectiveLatency = networkQualityParams.rtt + networkQualityParams.avgJitter * 2 + 10;

    // Standardized rating that ranges from 0 to 100, where higher values represent better call quality.
    let rFactor =
      effectiveLatency < 160 ? 93.2 - effectiveLatency / 40 : 93.2 - effectiveLatency / 120 - 10;

    // Packet loss should penalize the R factor
    // We are using Logarithmic Scaling in case the caller wants to reduce packet loss effect in the overall score
    const adjustedPacketLoss =
      networkQualityParams.packetsLoss > 0
        ? reducePacketLossEffect
          ? Math.log10(networkQualityParams.packetsLoss + 1)
          : networkQualityParams.packetsLoss
        : 0;

    rFactor -= adjustedPacketLoss * 2.5;
    return 1 + 0.035 * rFactor + 0.000007 * rFactor * (rFactor - 60) * (100 - rFactor);
  }

  private checkOutboundScores(
    outboundStats: NetworkQualityParams,
    isLocal: boolean,
  ): ParticipantNetworkState {
    const outboundMOS = this.calculateMOS(outboundStats, false);
    const outboundPacketLoss = outboundStats?.packetsLoss ?? 0;
    const outboundRTT = outboundStats?.rtt ?? 0;
    const outboundJitter = outboundStats?.avgJitter ?? 0;

    if (
      (outboundMOS > 0 &&
        outboundMOS <=
          this.getNetworkThreshold(TrafficDirection.OUTBOUND, NetworkMetricsThresholds.MOS)) ||
      outboundPacketLoss >=
        this.getNetworkThreshold(TrafficDirection.OUTBOUND, NetworkMetricsThresholds.PACKET_LOSS) ||
      outboundRTT >=
        this.getNetworkThreshold(TrafficDirection.OUTBOUND, NetworkMetricsThresholds.RTT) ||
      outboundJitter >=
        this.getNetworkThreshold(TrafficDirection.OUTBOUND, NetworkMetricsThresholds.JITTER)
    ) {
      return { isLocal, isNetworkStable: false };
    }

    return { isLocal, isNetworkStable: true };
  }

  // Skip the packet loss check for the inbound traffic as it will report the actual loss whatever your network state
  private checkInboundScores(
    inboundStats: NetworkQualityParams | undefined,
    isLocal: boolean,
  ): ParticipantNetworkState {
    const inboundMOS = this.calculateMOS(inboundStats, true);
    const inboundRTT = inboundStats?.rtt ?? 0;
    const inboundJitter = inboundStats?.avgJitter ?? 0;

    if (
      (inboundMOS > 0 &&
        inboundMOS <=
          this.getNetworkThreshold(TrafficDirection.INBOUND, NetworkMetricsThresholds.MOS)) ||
      inboundRTT >=
        this.getNetworkThreshold(TrafficDirection.INBOUND, NetworkMetricsThresholds.RTT) ||
      inboundJitter >=
        this.getNetworkThreshold(TrafficDirection.INBOUND, NetworkMetricsThresholds.JITTER)
    ) {
      return { isLocal, isNetworkStable: false };
    }
    return { isLocal, isNetworkStable: true };
  }

  private resetAllParticipantSystemStates() {
    this.participantSystemStateMap.managerKeys.getValue()?.forEach((participantId) => {
      this.participantSystemStateMap.get(participantId).next(null);
    });
  }

  private getNetworkThreshold(
    direction: TrafficDirection,
    metric: NetworkMetricsThresholds,
  ): number {
    try {
      const threshold = (
        this.flagsService.featureFlagsVariables[FLAGS.ENABLE_NETWORK_INDICATOR][
          `${direction}_thresholds`
        ] as any
      )?.[metric];

      return threshold ?? this.getDefaultThreshold(direction, metric);
    } catch (getFeatureFlagVariableError) {
      return this.getDefaultThreshold(direction, metric);
    }
  }

  private getDefaultThreshold(
    direction: TrafficDirection,
    metric: NetworkMetricsThresholds,
  ): number {
    const defaultThresholds =
      direction === TrafficDirection.INBOUND ? defaultInboundThresholds : defaultOutboundThresholds;
    return (defaultThresholds as any)[`${direction}_thresholds`][metric];
  }

  private getDevicePerformanceState(participant: CallParticipant) {
    if (this.shouldReflectDevicePerformanceState()) {
      return participant?.userSystemStatus?.isDeviceStruggling;
    }
  }

  // reflect_network_status_on_tiles controls showing the network indicator for VCT
  // and updates the system status panel
  private shouldReflectNetworkState() {
    return this.flagsService.featureFlagsVariables[FLAGS.ENABLE_NETWORK_INDICATOR]
      ?.reflect_network_status_on_tiles;
  }

  // reflect_devices_status controls showing the devices indicator for VCT (mic and cam)
  // and updates the system status panel
  private shouldReflectDevicesState() {
    return this.flagsService.featureFlagsVariables[FLAGS.ENABLE_NETWORK_INDICATOR]
      ?.reflect_devices_status;
  }

  // reflect_device_performance controls showing the device performance indicator in top navbar
  // and updates the system status panel
  private shouldReflectDevicePerformanceState() {
    return this.flagsService.featureFlagsVariables[FLAGS.ENABLE_NETWORK_INDICATOR]
      ?.reflect_device_performance;
  }

  public getParticipantDeviceState$ = (deviceType: DeviceType, participantId: string) =>
    this.participantSystemStateMap.get(participantId).pipe(
      untilDestroyed(this),
      filterNil(),
      map(
        (participantSystemState) =>
          participantSystemState?.devicesState?.[deviceType] ?? defaultMediaDeviceState,
      ),
      startWith(defaultMediaDeviceState),
    );

  public getParticipantNetworkStability$ = (participantId: string) =>
    this.participantSystemStateMap.get(participantId).pipe(
      untilDestroyed(this),
      filterNil(),
      map(
        (participantSystemState) => participantSystemState?.networkState?.isNetworkStable ?? true,
      ),
      startWith(true),
    );

  public getParticipantDevicePerformance$ = (participantId: string) =>
    this.participantSystemStateMap.get(participantId).pipe(
      untilDestroyed(this),
      filterNil(),
      map((participantSystemState) => participantSystemState?.isDeviceStruggling ?? false),
      startWith(false),
    );

  /**
   * Add current system status to analytics buffer
   */
  private addToSystemStatusBuffer(systemStatus: ParticipantSystemStatus): void {
    this.systemStatusAnalyticsService.addToSystemStatusBuffer({
      isNetworkStable: systemStatus.networkState?.isNetworkStable ?? true,
      isDeviceStruggling: systemStatus.isDeviceStruggling ?? false,
      micError: systemStatus.devicesState?.audio?.errorType,
      camError: systemStatus.devicesState?.video?.errorType,
    });
  }
}
